import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Eye,
  Pen,
  Plus,
  Trash2,
  Users,
  FileDown,
  RefreshCcw,
  ClipboardList,
  X,
  Mail,
  Phone,
  Building2,
} from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-toastify";
import Pagination from "../Pagination";
import "./CreateSalesPerson.css";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import LoadingScreen from "../Utils/LoadingScreen";
import "../Sales Person/CreateSalesPerson.css";

function AllSalesPerson() {
  const { Products = [], api } = useContext(GlobalContext);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [salesPersons, setSalesPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [detailData, setdetailData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!selectedProduct && Products?.length === 1) {
      setSelectedProduct(Products[0]?.pk_id ?? "");
    }
  }, [Products, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      fetchSalesPersons(selectedProduct, true);
    } else {
      const t = setTimeout(() => setPageLoading(false), 500);
      return () => clearTimeout(t);
    }
  }, [selectedProduct]);

  const productOptions = useMemo(
    () =>
      (Products || []).map((p) => ({
        value: p.pk_id ?? p.pkId ?? p.id ?? "",
        label: p.product_name ?? p.name ?? "Unknown",
      })),
    [Products]
  );

  const fetchSalesPersons = async (productId, isInitial = false) => {
    if (isInitial) {
      setPageLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const payload = { productId };
      const res = await api.post(`/Admin/v1.0/getAllSalesPersons`, payload);

      if (res.data?.responseCode === "00") {
        setSalesPersons(res.data.data || []);
        setCurrentPage(1);
      } else {
        toast.error(
          res.data?.responseMessage || "Failed to fetch sales persons"
        );
      }
    } catch (err) {
      toast.error("Something went wrong!");
    } finally {
      if (isInitial) {
        setTimeout(() => setPageLoading(false), 600);
      } else {
        setLoading(false);
      }
    }
  };

  const UpdateSalesPersonsStatus = async (salesPerson, currentStatus) => {
    const userId = salesPerson.code;
    const newStatus = currentStatus ? "Inactive" : "Active";

    if (updatingUserId === userId) return;
    setUpdatingUserId(userId);

    try {
      const payload = {
        adminId: selectedProduct,
        userId: userId,
        status: newStatus,
      };

      const res = await api.post(
        `/Admin/v1.0/updateSalesPersonStatus`,
        payload
      );

      if (res.data?.responseCode === "00") {
        setSalesPersons((prev) =>
          prev.map((sp) =>
            sp.code === userId ? { ...sp, status: newStatus } : sp
          )
        );
        toast.success(`Status updated to ${newStatus}`);
      } else {
        toast.error(res.data?.responseMessage || "Failed to update status");
      }
    } catch (err) {
      toast.error("Something went wrong!");
      console.error("Status update error:", err);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAdd = () => {
    window.location.assign("/create-sales-person");
  };

  const handleView = (row) => {
    toast.info(`Viewing: ${row?.name || "Sales Person"}`);
  };

  const handleEdit = (row) => {
    toast.info(`Editing: ${row?.name || "Sales Person"}`);
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete sales person "${row?.name}"?`);
    if (!ok) return;

    try {
      toast.success("Deleted successfully (wire your API here)");
      if (selectedProduct) fetchSalesPersons(selectedProduct);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleRefresh = () => {
    if (selectedProduct) {
      setPageLoading(true);
      fetchSalesPersons(selectedProduct, true);
    }
  };

  const filteredSalesPersons = useMemo(() => {
    return salesPersons.filter((sp) => {
      if (sp.dob) {
        const dobDate = new Date(sp.dob);
        if (fromDate && dobDate < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (dobDate > toEnd) return false;
        }
      }

      if (search.trim() !== "") {
        const s = search.toLowerCase();
        const fields = [sp.name, sp.mobile, sp.code, sp.region, sp.email];
        if (!fields.some((f) => f?.toLowerCase().includes(s))) return false;
      }

      return true;
    });
  }, [salesPersons, fromDate, toDate, search]);

  const openDetails = (row) => setdetailData(row);
  const closeDetails = () => setdetailData(null);

  const paginatedSalesPersons = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredSalesPersons.slice(start, end);
  }, [filteredSalesPersons, currentPage]);

  if (pageLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="aoc-csp-screen w-full bg-gradient-to-b from-gray-50 to-white rounded-2xl min-h-[90svh]">
      <div className="aoc-csp-container px-4 py-6 md:py-4">
        <div className="aoc-csp-header flex items-center justify-between gap-4 mb-6 md:mb-6 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 shadow-sm">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
                Sales Persons
              </h1>
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div className="min-w-[160px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email..."
                className="aoc-csp-control-new "
              />
            </div>
            {/* <div className="flex items-center gap-3">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-blue-800 dark:text-gray-100"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100"
              />
            </div> */}

            <div className="flex items-end gap-3 flex-wrap">
              <button className="btn btn-refresh" onClick={handleRefresh}>
                <RefreshCcw size={14} />
                <span>Refresh</span>
              </button>

              <button
                className="btn btn-export"
                onClick={() =>
                  exportAnyJsonToExcel({
                    data: filteredSalesPersons,
                    filePrefix: "allsalesPersonExport",
                    sheetName: "allsalesPersonList",
                  })
                }
              >
                <FileDown size={14} />
                <span>Export</span>
              </button>

              <div className="min-w-[100px]">
                <label className="aoc-csp-label ml-2">Product</label>
                <div className="aoc-csp-control-new">
                  <select
                    className="aoc-csp-input w-[100px] text-sm rounded outline-none"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                  >
                    <option value="">Select product</option>
                    {productOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleAdd}
                  className="aoc-csp-btn-add"
                >
                  <Plus className="h-4 w-4" />
                  Sales Person
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="aoc-csp-card relative rounded-2xl animate-rise">
          <div className="aoc-csp-table-wrapper overflow-x-auto">
            <table className="aoc-csp-table w-full">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Code</th>
                  <th>Region</th>
                  <th>Email</th>
                  <th>Dob</th>
                  <th>Status</th>
                  <th>Status</th>
                  <th className="w-[140px] text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={`sk-${i}`} className="aoc-csp-skel-row">
                      <td>
                        <div className="aoc-csp-skel w-32" />
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-24" />
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-16" />
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-20" />
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-36" />
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-24" />
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="aoc-csp-skel h-6 w-10 rounded-full" />
                        </div>
                      </td>
                      <td>
                        <div className="aoc-csp-skel w-20" />
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="aoc-csp-skel h-6 w-6 rounded-full" />
                          <div className="aoc-csp-skel h-6 w-6 rounded-full" />
                          <div className="aoc-csp-skel h-6 w-6 rounded-full" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : paginatedSalesPersons.length > 0 ? (
                  paginatedSalesPersons.map((sp, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="text-gray-900">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td className="text-gray-900">{sp.dob}</td>
                      <td className="text-gray-900">{sp.name}</td>
                      <td className="text-gray-700">{sp.mobile}</td>
                      <td className="text-gray-700">{sp.code}</td>
                      <td className="text-gray-700">{sp.region}</td>
                      <td className="text-gray-700">{sp.email}</td>
                      <td className="text-gray-700">
                        {sp.dob ? new Date(sp.dob).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {(() => {
                          const userCode = sp.code;
                          const isOn = sp.status === "Active";
                          const disabled = updatingUserId === userCode;

                          return (
                            <label
                              className={`relative inline-flex items-center ${
                                disabled
                                  ? "opacity-60 pointer-events-none"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isOn}
                                onChange={() =>
                                  !disabled &&
                                  UpdateSalesPersonsStatus(sp, isOn)
                                }
                                disabled={disabled}
                                aria-checked={isOn}
                                aria-label="Toggle duty status"
                              />

                              <div
                                className={[
                                  "w-10 h-5 rounded-full transition-colors duration-300",
                                  "bg-gray-300 peer-checked:bg-blue-500",
                                  "ring-0 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-400",
                                ].join(" ")}
                              />

                              <span
                                className={[
                                  "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow",
                                  "transition-transform duration-300",
                                  "peer-checked:translate-x-[1.25rem]",
                                ].join(" ")}
                              />
                            </label>
                          );
                        })()}
                      </td>

                      <td className="text-gray-700">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm ${
                            sp.status === "Active"
                              ? "bg-green-500"
                              : sp.status === "Inactive"
                              ? "bg-red-500"
                              : "bg-gray-400"
                          }`}
                        >
                          {sp.status === "Active"}
                          {sp.status === "Inactive"}
                          {!["Active", "Inactive"].includes(sp.status)}
                          {sp.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            className="aoc-csp-icon-btn text-gray-600 hover:text-gray-900"
                            title="View"
                            onClick={() => openDetails(sp)}
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            className="aoc-csp-icon-btn text-blue-600 hover:text-blue-800"
                            title="Edit"
                            onClick={() => handleEdit(sp)}
                          >
                            <Pen className="h-4 w-4" />
                          </button>
                          <button
                            className="aoc-csp-icon-btn text-red-600 hover:text-red-700"
                            title="Delete"
                            onClick={() => handleDelete(sp)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-10">
                      <div className="flex flex-col items-center justify-center text-center">
                        <p className="text-gray-700 font-medium">
                          No sales persons found
                        </p>
                        <p className="text-gray-500 text-sm">
                          {selectedProduct
                            ? "Try adding a new sales person."
                            : "Please select a product first."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {detailData && (
        <div className="aoc-csp-modal" role="dialog" aria-modal="true">
          <div className="aoc-csp-modal-backdrop" onClick={closeDetails} />
          <div className="aoc-csp-modal-card animate-rise">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-100 grid place-items-center border border-gray-200">
                  <ClipboardList className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Sales Person Details
                  </h3>
                </div>
              </div>
              <button
                className="aoc-csp-icon-btn"
                onClick={closeDetails}
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="Name" value={detailData.name || "N/A"} />
              <InfoRow
                icon={<Phone className="h-4 w-4" />}
                label="Contact Mobile"
                value={detailData.mobile || "N/A"}
              />
              <InfoRow
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                value={detailData.email || "N/A"}
              />
              <InfoRow label="Gender" value={detailData.gender || "N/A"} />
              <InfoRow label="Pan Card" value={detailData.pan_card || "N/A"} />
              <InfoRow
                label="Aadhaar Card"
                value={detailData.aadhaar_card || "N/A"}
              />
              <InfoRow
                label="Maritial Status"
                value={detailData.maritial_status || "N/A"}
              />
              <InfoRow
                label="Blood Group"
                value={detailData.blood_group || "N/A"}
              />
              <InfoRow
                icon={<Building2 className="h-4 w-4" />}
                label="Department"
                value={detailData.department || "N/A"}
              />
              <InfoRow
                label="Designation"
                value={detailData.designation || "N/A"}
              />
              <InfoRow label="DOB" value={detailData.dob || "N/A"} />
              <InfoRow label="Code" value={detailData.code || "N/A"} />
              <InfoRow label="Region" value={detailData.region || "N/A"} />
              <InfoRow
                label="Emergency Name"
                value={detailData.emergency_name || "N/A"}
              />
              <InfoRow
                label="Emergency Mobile"
                value={detailData.emergency_mobile || "N/A"}
              />
              <InfoRow
                label="Emergency Region"
                value={detailData.emergency_region || "N/A"}
              />
              <InfoRow
                label="Reporting Manager"
                value={detailData.reporting_manager || "N/A"}
              />
              <InfoRow
                label="Vaccination Status"
                value={detailData.vaccination_status || "N/A"}
              />
              <div className="md:col-span-2">
                <div className="aoc-csp-label mb-1">Office Address</div>
                <div className="aoc-csp-control">
                  <textarea
                    className="aoc-csp-input min-h-[80px]"
                    readOnly
                    value={detailData.office_address || "N/A"}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="aoc-csp-label mb-1">Home Address</div>
                <div className="aoc-csp-control">
                  <textarea
                    className="aoc-csp-input min-h-[80px]"
                    readOnly
                    value={detailData.home_address || "N/A"}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <div className="aoc-csp-label mb-2">Profile Image</div>
              <div className="grid grid-cols-1 gap-4 max-w-[200px]">
                <ImageTile
                  label="Profile Image"
                  url={detailData.profile_image}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredSalesPersons.length > itemsPerPage && (
        <div className="mt-4 flex justify-center">
          <Pagination
            totalItems={filteredSalesPersons.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div>
      <div className="aoc-csp-label mb-1">{label}</div>
      <div className="aoc-csp-control">
        {icon ? <span className="aoc-csp-icon">{icon}</span> : null}
        <input className="aoc-csp-input" value={value || "N/A"} readOnly />
      </div>
    </div>
  );
}

function ImageTile({ label, url }) {
  const [imageError, setImageError] = useState(false);
  const isValid =
    !!url && typeof url === "string" && url.trim() !== "" && url !== "string";

  const handleClick = () => {
    if (isValid && !imageError) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const showImage = isValid && !imageError;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 ${
          showImage
            ? "cursor-pointer hover:border-blue-400 transition-colors"
            : ""
        }`}
        onClick={handleClick}
      >
        {showImage ? (
          <img
            src={url}
            alt={label}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 text-sm font-medium">No Image</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllSalesPerson;
